import { useMemo } from 'react'
import { Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Scatter, ComposedChart } from 'recharts'
import type { MarketHistoryItem, RunHistoryItem } from '../types'
import { formatProbability, formatDateTime, formatPnL } from '../utils/format'
import { parseISO } from 'date-fns'

interface TradeAnnotation {
  timestamp: string
  side: 'buy' | 'sell'
  price?: number
  qty?: number
  notional?: number
}

interface PriceChartProps {
  marketHistory: MarketHistoryItem[]
  runs: RunHistoryItem[]
  trades?: TradeAnnotation[]
  showLegend?: boolean
}

export function PriceChart({ marketHistory, runs, trades = [], showLegend = true }: PriceChartProps) {
  const chartData = useMemo(() => {
    // Combine market history and runs
    const data: {
      timestamp: number
      market?: number
      prediction?: number
      label?: string
      trade?: 'buy' | 'sell'
      tradeNotional?: number
    }[] = []

    // Add market prices
    marketHistory.forEach((item) => {
      if (item.asof_ts && item.prob !== undefined) {
        data.push({
          timestamp: parseISO(item.asof_ts).getTime(),
          market: item.prob,
        })
      }
    })

    // Add prediction points
    runs.forEach((run) => {
      if (run.started_at && run.post_prob !== undefined) {
        const timestamp = parseISO(run.started_at).getTime()
        const existing = data.find((d) => Math.abs(d.timestamp - timestamp) < 60000)
        if (existing) {
          existing.prediction = run.post_prob
          existing.label = `Run #${run.run_id}`
        } else {
          data.push({
            timestamp,
            prediction: run.post_prob,
            label: `Run #${run.run_id}`,
          })
        }
      }
    })

    // Add trade annotations
    trades.forEach((trade) => {
      if (trade.timestamp) {
        const timestamp = parseISO(trade.timestamp).getTime()
        const existing = data.find((d) => Math.abs(d.timestamp - timestamp) < 60000)
        if (existing) {
          existing.trade = trade.side
          existing.tradeNotional = trade.notional
        } else {
          data.push({
            timestamp,
            market: trade.price,
            trade: trade.side,
            tradeNotional: trade.notional,
          })
        }
      }
    })

    // Sort by timestamp
    data.sort((a, b) => a.timestamp - b.timestamp)

    return data
  }, [marketHistory, runs, trades])
  
  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-star-muted font-mono text-sm">
        No price history available
      </div>
    )
  }
  
  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="marketGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#00d4ff" stopOpacity={0} />
            </linearGradient>
          </defs>
          
          <XAxis
            dataKey="timestamp"
            tickFormatter={(ts) => {
              const date = new Date(ts)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 10, fontFamily: 'monospace' }}
          />
          
          <YAxis
            domain={[0, 1]}
            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 10, fontFamily: 'monospace' }}
            width={40}
          />
          
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null
              const data = payload[0].payload
              return (
                <div className="glass-card p-2 text-xs font-mono">
                  <div className="text-star-muted mb-1">
                    {formatDateTime(new Date(data.timestamp).toISOString())}
                  </div>
                  {data.market !== undefined && (
                    <div className="text-signal-blue">
                      Market: {formatProbability(data.market)}
                    </div>
                  )}
                  {data.prediction !== undefined && (
                    <div className="text-signal-green">
                      Prediction: {formatProbability(data.prediction)}
                    </div>
                  )}
                  {data.label && (
                    <div className="text-star-muted">{data.label}</div>
                  )}
                  {data.trade && (
                    <div className={data.trade === 'buy' ? 'text-signal-green' : 'text-signal-red'}>
                      {data.trade === 'buy' ? '📈 BUY' : '📉 SELL'}
                      {data.tradeNotional && ` ${formatPnL(data.tradeNotional)}`}
                    </div>
                  )}
                </div>
              )
            }}
          />
          
          <ReferenceLine y={0.5} stroke="#555566" strokeDasharray="3 3" />
          
          <Line
            type="monotone"
            dataKey="market"
            stroke="#00d4ff"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          
          <Scatter
            dataKey="prediction"
            fill="#00ff88"
            stroke="#00ff88"
            shape={(props: unknown) => {
              const p = props as { cx?: number; cy?: number }
              if (p.cx === undefined || p.cy === undefined) return <></>
              return (
                <circle
                  cx={p.cx}
                  cy={p.cy}
                  r={4}
                  fill="#00ff88"
                  stroke="#0a0a0f"
                  strokeWidth={2}
                />
              )
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      {showLegend && (
        <div className="flex items-center justify-center gap-6 mt-2 text-[10px] font-mono">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-[#00d4ff]" />
            <span className="text-star-muted">Market Price</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#00ff88]" />
            <span className="text-star-muted">Our Prediction</span>
          </div>
          {trades.length > 0 && (
            <>
              <div className="flex items-center gap-1">
                <span className="text-signal-green">▲</span>
                <span className="text-star-muted">Buy</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-signal-red">▼</span>
                <span className="text-star-muted">Sell</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}


